import React, { useEffect, useState } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Button,
  Select,
  MenuItem,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import TableComponent from 'Component/Table';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllAvaiableTraineesForApproval,
  submitTraineeStatus,
  updateTrainingTask,
} from 'thunks/trainingThunks/thunk';
import { updateTraineeStatus } from 'reducer/traineeApprovalSlice';
import useCredentials from 'hooks/useCredentials';
import { ROLES, AUTHORIZED_EMPLOYEES } from 'Constants/roles';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useIsLearningRMCH from 'hooks/useIsLearningRMCH';
const TraineeApproval = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData: user } = useCredentials();
  const { isRM, isCH } = useIsLearningRMCH();
  useEffect(() => {
    // if (isRM) {
    //   if (AUTHORIZED_EMPLOYEES.includes(user?.employeeId)) {
    //     return;
    //   } else {
    //     navigate('/unauthorized');
    //   }
    // }
  }, [user, navigate, isRM]);

  const { id: batchId } = useParams();

  const [selectedActions, setSelectedActions] = useState({});
  const [declineReason, setDeclineReason] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [currentEmpId, setCurrentEmpId] = useState(null);
  const [openSubmitDialog, setOpenSubmitDialog] = useState(false);

  const {
    data: { trainees, trainesSubmited, submittedBy },
    loading,
  } = useSelector((state) => state.traineeApprovalSlice);
  const submitedByCurrentUser = submittedBy.includes(String(user?.employeeId));
  useEffect(() => {
    dispatch(fetchAllAvaiableTraineesForApproval({ batchId, role: 'RM', type: 'trainee' }));
  }, [batchId, dispatch]);

  // Add effect to run handleAllowAll only on initial load
  useEffect(() => {
    if (trainees?.length > 0 && !loading && Object.keys(selectedActions).length === 0) {
      handleAllowAll();
    }
  }, [trainees, loading]);
  useEffect(() => {
    if (trainesSubmited || submitedByCurrentUser) {
      // mark the task as completed
      dispatch(
        updateTrainingTask({
          approvalAuthority: Number(user?.employeeId),
          batchId,
          step: 1,
        })
      );
    }
  }, [trainesSubmited, dispatch, submitedByCurrentUser]);
  const handleActionChange = (empId, value) => {
    setSelectedActions((prev) => ({ ...prev, [empId]: value }));

    if (value === 'Declined') {
      setCurrentEmpId(empId);
      setOpenModal(true);
    } else {
      dispatch(updateTraineeStatus({ empId, allowed: value === 'Allowed', reason: null }));
    }
  };

  const handleCloseModal = () => {
    setSelectedActions((prev) => ({ ...prev, [currentEmpId]: '' }));
    setOpenModal(false);
    setDeclineReason('');
    setCurrentEmpId(null);
  };

  const handleDeclineSubmit = () => {
    dispatch(
      updateTraineeStatus({
        empId: currentEmpId,
        allowed: false,
        reason: declineReason || 'No reason provided',
      })
    );
    setOpenModal(false);
    setDeclineReason('');
  };

  const handleAllowAll = () => {
    const updatedActions = {};
    trainees.forEach((t) => {
      updatedActions[t.empId] = 'Allowed';
      dispatch(updateTraineeStatus({ empId: t.empId, allowed: true, reason: null }));
    });
    setSelectedActions(updatedActions);
  };

  const handleClearAll = () => {
    const clearedActions = {};
    trainees.forEach((t) => {
      clearedActions[t.empId] = '';
    });
    setSelectedActions(clearedActions);
  };

  const handleSubmit = () => {
    const allSelected = trainees.every((t) => selectedActions[t.empId]);
    if (!allSelected) {
      toast.error('Please select an action for all trainees before submitting.', {
        theme: 'colored',
      });
      return;
    }
    setOpenSubmitDialog(true);
  };

  const handleSubmitConfirm = () => {
    if (!batchId) {
      toast.error('No batch found', { theme: 'colored' });
      return;
    }
    dispatch(
      submitTraineeStatus({
        batchId,
        step: 1,
        payload: trainees,
        approvalAuthority: Number(user?.employeeId),
      })
    );
    toast.success('Trainee approvals submitted successfully!', { theme: 'colored' });
    setOpenSubmitDialog(false);
  };

  const columns = [
    { label: 'Employee ID', field: 'empId' },
    { label: 'Employee Name', field: 'empName' },
    { label: 'Region', field: 'region' },
    { label: 'Branch', field: 'branch' },
    { label: 'Role', field: 'role' },
    { label: 'Nomination Count', field: 'nominationCount' },
    {
      label: 'Action',
      field: 'allowed',
      render: (row) => (
        <Select
          value={selectedActions[row.empId] || ''}
          onChange={(e) => handleActionChange(row.empId, e.target.value)}
          displayEmpty
          size="small"
          sx={{ width: 120 }}
        >
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="Allowed">Allowed</MenuItem>
          <MenuItem value="Declined">Declined</MenuItem>
        </Select>
      ),
    },
  ];

  return (
    <Layout>
      <Box
        sx={{
          fontSize: '24px',
          fontWeight: 500,
          background: 'linear-gradient(to bottom, #E9FBE0 0%, #E6F0E8 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
        }}
      >
        {`Available Trainees for BatchID: ${batchId}`}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-around', my: 2 }}>
        <Button variant="contained" onClick={handleAllowAll}>
          Allow All
        </Button>
        <Button variant="contained" onClick={handleClearAll}>
          Clear All
        </Button>
        <Box sx={{ position: 'relative', display: 'inline-block', justifyItems: 'center' }}>
          <Button
            variant="contained"
            disabled={trainesSubmited || submitedByCurrentUser}
            onClick={handleSubmit}
          >
            {trainesSubmited ? 'Submitted' : 'Submit'}
          </Button>
        </Box>
      </Box>
      {submitedByCurrentUser && (
        <Alert
          severity="info"
          sx={{
            mt: 2,
            mb: 2,
            '& .MuiAlert-message': {
              fontSize: '14px',
              fontWeight: 500,
            },
          }}
        >
          Batch approval has already been submitted
        </Alert>
      )}
      <Box mt={2}>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <TableComponent
            tableData={trainees}
            columns={columns}
            tableName="TraineeList"
            count={trainees?.length}
            useWholeRowClicked={false}
            enableLocalSearch={true}
          />
        )}
      </Box>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Enter Reason To Decline</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Please provide a reason for declining this trainee:
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            sx={{ mt: 2 }}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleDeclineSubmit} disabled={!declineReason.trim()}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSubmitDialog}
        onClose={() => setOpenSubmitDialog(false)}
        aria-labelledby="submit-dialog-title"
        aria-describedby="submit-dialog-description"
      >
        <DialogTitle id="submit-dialog-title">Confirm Submission</DialogTitle>
        <DialogContent>
          <DialogContentText id="submit-dialog-description">
            Are you sure you want to submit the approvals for all trainees?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubmitDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitConfirm} autoFocus>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default TraineeApproval;
